import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  Checkbox,
  TextField,
  RadioGroup,
  Typography,
  FormHelperText,
  Grid,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { signUpThunk } from "../../../redux/thunks/auth.thunk";
import { createBusinessThunk } from "../../../redux/thunks/user.thunk";
import Joi from "joi";
import { IWithClassName } from "../../../theme/default.theme";
import { BusinessType } from "../../../enum/business";
import { TabEnum } from "../../../component/auth-frame/component";
import AuthFrame from "../../../component/auth-frame";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import { ReactComponent as Advertiser } from "../../../svg/advertiser.svg";
import { ReactComponent as Tree } from "../../../svg/tree.svg";
import { useNavigate } from "react-router-dom";
import { getCountriesThunk } from "../../../redux/thunks/global.thunk";
import { createSelector } from "@reduxjs/toolkit";
import { IRootState } from "../../../redux/store";

const selector = createSelector(
  (state: IRootState) => state.global,
  (state: IRootState) => state.auth,
  (global, auth) => ({
    countries: global.countries.data ?? [],
    signUp: auth.signUp,
    userId: auth.userId,
  }),
);

const Signup = ({ className }: IWithClassName) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { signUp, userId, countries } = useAppSelector(selector);

  useEffect(() => {
    dispatch(getCountriesThunk());
  }, [dispatch]);

  const schema = Joi.object({
    companyName: Joi.string().required().messages({
      "string.empty": "Company name is required",
    }),
    name: Joi.string().required().messages({
      "string.empty": "Name is required",
    }),
    email: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        "string.email": "Email must be a valid email",
        "string.empty": "Email is required",
      }),
    password: Joi.string().min(8).max(20).required().messages({
      "string.min": "Password must be at least 8 characters",
      "string.max": "Password must be at most 20 characters",
      "string.empty": "Password is required",
    }),
    confirmPassword: Joi.string()
      .valid(Joi.ref("password"))
      .required()
      .messages({
        "string.empty": "Confirm password is required",
        "any.only": "Confirm password must match password",
      }),
    country: Joi.string().required().messages({
      "string.empty": "Country is required",
    }),
    businessType: Joi.string()
      .valid(BusinessType.Agency, BusinessType.DirectApp)
      .required()
      .messages({
        "string.empty": "Business type is required",
      }),
    termAndCondition: Joi.boolean().valid(true).required().messages({
      "any.only": "You must accept the terms and conditions",
    }),
  });

  useEffect(() => {
    setError((state) => ({
      ...state,
      email: signUp.error,
    }));

    if (userId) {
      navigate("/sign-up/acknowledgement");
    }
  }, [signUp.error, userId, navigate]);

  const [signupFrom, setSignupForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    companyName: "",
    businessType: BusinessType.Agency,
    country: "",
    termAndCondition: false,
  });
  const [error, setError] = useState<{
    country?: string;
    companyName?: string;
    name?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
    termAndCondition?: string;
  }>({});

  const onClick = () => {
    setError({});

    schema
      .validateAsync(signupFrom)
      .then(
        async ({
          email,
          password,
          name,
          companyName,
          country,
          businessType,
        }) => {
          await dispatch(signUpThunk({ email, password, name }));
          await dispatch(
            createBusinessThunk({
              companyName,
              type: businessType,
              country,
              name,
              email,
            }),
          );
        },
      )
      .catch((err) => {
        setError((state) => ({
          ...state,
          [err.details[0].path[0]]: err.details[0].message,
        }));
      });
  };

  return (
    <AuthFrame tab={TabEnum.SING_UP}>
      <FormControl className={className}>
        <Box className="full-width radio-container">
          <Typography>I am</Typography>
          <RadioGroup
            className="user-type"
            defaultValue={BusinessType.Agency}
            onChange={({ currentTarget }) =>
              setSignupForm((state) => ({
                ...state,
                businessType: currentTarget.value as BusinessType,
              }))
            }
            row
          >
            <FormControlLabel
              value={BusinessType.Agency}
              control={
                <Radio
                  icon={<Advertiser className="tree-icon" />}
                  checkedIcon={<Advertiser className="tree-icon" />}
                />
              }
              label="Agency"
            />
            <FormControlLabel
              value={BusinessType.DirectApp}
              control={
                <Radio
                  icon={<Tree className="tree-icon" />}
                  checkedIcon={<Tree className="tree-icon" />}
                />
              }
              label="Direct App"
            />
          </RadioGroup>
        </Box>

        <Box className="full-width">
          <TextField
            label="Company Name"
            error={!!error.companyName}
            helperText={error.companyName}
            onChange={({ currentTarget }) =>
              setSignupForm((state) => ({
                ...state,
                companyName: currentTarget.value,
              }))
            }
          />
        </Box>

        <Box className="half-width">
          <TextField
            label="Name"
            error={!!error.name}
            helperText={error.name}
            onChange={({ currentTarget }) =>
              setSignupForm((state) => ({
                ...state,
                name: currentTarget.value,
              }))
            }
          />
          <TextField
            label="Email"
            error={!!error.email}
            helperText={error.email}
            onChange={({ currentTarget }) =>
              setSignupForm((state) => ({
                ...state,
                email: currentTarget.value,
              }))
            }
            InputProps={{
              endAdornment: <MailOutlinedIcon />,
            }}
          />
        </Box>

        <Box className="half-width">
          <TextField
            label="Password"
            error={!!error.password}
            helperText={error.password}
            type="password"
            onChange={({ currentTarget }) =>
              setSignupForm((state) => ({
                ...state,
                password: currentTarget.value,
              }))
            }
          />
          <TextField
            label="Confirm Password"
            error={!!error.confirmPassword}
            helperText={error.confirmPassword}
            type="password"
            onChange={({ currentTarget }) =>
              setSignupForm((state) => ({
                ...state,
                confirmPassword: currentTarget.value,
              }))
            }
          />
        </Box>

        <Box className="full-width radio-container">
          <FormControl>
            <InputLabel id="demo-simple-select-label">Country</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={signupFrom.country}
              label="Country"
              onChange={({ target }) =>
                setSignupForm((state) => ({
                  ...state,
                  country: target.value,
                }))
              }
            >
              {countries?.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText error={!!error.country}>
              {error.country}
            </FormHelperText>
          </FormControl>
          {/* <CountrySelector
            error={error.country}
            value={signupFrom.country}
            onchange={(value) =>
              setSignupForm((state) => ({
                ...state,
                country: value,
              }))
            }
            label="Country"
            placeholder="Country"
          /> */}
        </Box>

        <Box className="full-width tc-container">
          <FormControlLabel
            control={
              <Checkbox
                onChange={({ target }) =>
                  setSignupForm((state) => ({
                    ...state,
                    termAndCondition: target.checked,
                  }))
                }
              />
            }
            label={
              <Grid className="tc-label">
                <Typography>
                  I accept <a href="/sign-up">Terms and Conditions</a>
                </Typography>
                {error.termAndCondition && (
                  <FormHelperText error>
                    {error.termAndCondition}
                  </FormHelperText>
                )}
              </Grid>
            }
          />
        </Box>

        <Button
          variant="contained"
          onClick={onClick}
          startIcon={<MarkEmailReadOutlinedIcon />}
        >
          Sign Up
        </Button>
      </FormControl>
    </AuthFrame>
  );
};

export default Signup;
